import React, { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { Alert, Box, Button, CircularProgress, Grid, Link, MenuItem, Paper, Stack, TextField, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SettingsIcon from '@mui/icons-material/Settings';
import ResponsiveTable, { ResponsiveTableColumn } from '../components/common/ResponsiveTable';
import NewEnquiryDialog from '../components/e2i/NewEnquiryDialog';
import { RegisterChip, StatusChip } from '../components/e2i/StatusChip';
import useCompanyId from '../components/e2i/useCompanyId';
import { useIsMobile } from '../hooks/useBreakpoints';
import e2iApi from '../services/e2iApi';
import type { E2IConfig, EnquiryRow, EnquiryStatus, QuotationStatus } from '../types/e2i';
import { ENQUIRY_STATUS, ENQUIRY_TYPES, QUOTATION_STATUS, REGISTER_CODES, REGISTER_NAMES } from '../types/e2i';
import { cleanDateInput, dateOrNull, errorText, formatAmount, formatDate, quotationLabel, registerLabel } from '../utils/e2i';

/** Enquiry to Invoice › Enquiries: the company's enquiries with their register status, and new ones. */
const E2IEnquiries: React.FC = () => {
  const navigate = useNavigate();
  const mobile = useIsMobile();
  const { companyId, companyName } = useCompanyId();
  const [config, setConfig] = useState<E2IConfig | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [rows, setRows] = useState<EnquiryRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [listError, setListError] = useState<string | null>(null);
  const [status, setStatus] = useState<'' | EnquiryStatus>('');
  const [register, setRegister] = useState<string>('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [newOpen, setNewOpen] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(search.trim()), 400);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    if (!companyId) { setError('Select a company first.'); setConfig(null); return undefined; }
    let cancelled = false;
    setError(null);
    setConfig(null);
    e2iApi.config(companyId)
      .then((c) => { if (!cancelled) setConfig(c); })
      .catch((e: any) => { if (!cancelled) setError(errorText(e, 'Enquiry to Invoice could not be opened for this company')); });
    return () => { cancelled = true; };
  }, [companyId]);

  const fromDate = dateOrNull(from) || undefined;
  const toDate = dateOrNull(to) || undefined;
  useEffect(() => {
    if (!companyId || !config) return undefined;
    let cancelled = false;
    setLoading(true);
    setListError(null);
    e2iApi.listEnquiries(companyId, { status: status || undefined, register: register || undefined, q: debounced || undefined, from: fromDate, to: toDate })
      .then((r) => { if (!cancelled) setRows(r); })
      .catch((e: any) => { if (!cancelled) { setRows([]); setListError(errorText(e, 'The enquiries could not be loaded')); } })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [companyId, config, status, register, debounced, fromDate, toDate]);

  const currency = config?.currency || '';
  const registerCodes = config?.registerCodes?.length ? config.registerCodes : REGISTER_CODES.map((c) => ({ code: c, name: REGISTER_NAMES[c] }));

  const columns: ResponsiveTableColumn<EnquiryRow>[] = useMemo(() => [
    {
      id: 'no',
      header: 'Enquiry',
      width: 150,
      cell: (r) => (
        <Box>
          {/* On a phone the whole card opens the enquiry (a link inside it would nest two controls). */}
          {mobile ? (
            <Typography variant="body2" fontWeight={600}>{r.enquiryNo || 'Draft'}</Typography>
          ) : (
            <Link component={RouterLink} to={`/e2i/enquiries/${r.enquiryId}`} underline="hover" fontWeight={600} onClick={(ev: React.MouseEvent) => ev.stopPropagation()}>
              {r.enquiryNo || 'Draft'}
            </Link>
          )}
          <Typography variant="caption" color="text.secondary" display="block">{formatDate(r.enquiryDate)}</Typography>
        </Box>
      ),
    },
    {
      id: 'customer',
      header: 'Customer',
      cell: (r) => (
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2">{r.customer?.name || r.customer?.code}</Typography>
          {r.project ? <Typography variant="caption" color="text.secondary">{r.project.code}{r.project.name ? ` ${r.project.name}` : ''}</Typography> : null}
        </Box>
      ),
    },
    {
      id: 'subject',
      header: 'Subject',
      cell: (r) => (
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2">{r.subject}</Typography>
          <Typography variant="caption" color="text.secondary">{ENQUIRY_TYPES.find((t) => t.value === r.enquiryType)?.label || r.enquiryType}</Typography>
        </Box>
      ),
    },
    {
      id: 'status',
      header: 'Status',
      cell: (r) => (
        <Stack spacing={0.5} alignItems="flex-start">
          <StatusChip status={ENQUIRY_STATUS[r.status]} fallback={r.status} />
          {r.registerStatus ? <RegisterChip code={r.registerStatus} name={r.registerName} config={config} /> : null}
        </Stack>
      ),
    },
    {
      id: 'quotation',
      header: 'Latest quotation',
      hideOnMobile: true,
      cell: (r) => (r.latestQuotation ? (
        <Box>
          <Typography variant="body2">{quotationLabel(r.latestQuotation.quotationNo, r.latestQuotation.revision)}</Typography>
          <Typography variant="caption" color="text.secondary">
            {QUOTATION_STATUS[r.latestQuotation.status as QuotationStatus]?.label || r.latestQuotation.status} · {formatAmount(r.latestQuotation.total)} {currency}
          </Typography>
        </Box>
      ) : '—'),
    },
    { id: 'job', header: 'Job', hideOnMobile: true, cell: (r) => r.jobNo || '—' },
    { id: 'invoiced', header: `Invoiced ${currency}`.trim(), align: 'right', hideOnMobile: true, cell: (r) => (r.invoicedTotal ? formatAmount(r.invoicedTotal) : '—') },
    { id: 'updated', header: 'Updated', hideOnMobile: true, cell: (r) => formatDate(r.updatedAt) },
  ], [config, currency, mobile]);

  const filtered = !!(status || register || debounced || fromDate || toDate);

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 1400 }}>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
        <Typography variant="h5" component="h1" sx={{ fontWeight: 600 }}>Enquiries</Typography>
        {companyName ? <Typography variant="body2" color="text.secondary">{companyName}</Typography> : null}
        <Box sx={{ flexGrow: 1 }} />
        {config?.capabilities.setup ? <Button variant="outlined" startIcon={<SettingsIcon />} onClick={() => navigate('/e2i/setup')}>Setup</Button> : null}
        {config ? <Button startIcon={<AddIcon />} onClick={() => setNewOpen(true)} disabled={!config.enabled}>New enquiry</Button> : null}
      </Stack>

      {error ? <Alert severity="error">{error}</Alert> : null}
      {config && !config.enabled ? (
        <Alert severity="warning">Enquiry to Invoice is not switched on for {companyName || 'this company'}. An administrator switches it on in Company Management.</Alert>
      ) : null}

      {config ? (
        <>
          <Paper sx={{ p: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <TextField id="e2i-enq-search" label="Search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Number, customer, project or subject" fullWidth />
              </Grid>
              <Grid item xs={6} md={2}>
                <TextField id="e2i-enq-status" select label="Status" value={status} onChange={(e) => setStatus(e.target.value as '' | EnquiryStatus)} fullWidth SelectProps={{ displayEmpty: true }} InputLabelProps={{ shrink: true }}>
                  <MenuItem value="">All</MenuItem>
                  {(Object.keys(ENQUIRY_STATUS) as EnquiryStatus[]).map((s) => <MenuItem key={s} value={s}>{ENQUIRY_STATUS[s].label}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid item xs={6} md={2}>
                <TextField id="e2i-enq-register" select label="Register status" value={register} onChange={(e) => setRegister(String(e.target.value))} fullWidth SelectProps={{ displayEmpty: true }} InputLabelProps={{ shrink: true }}>
                  <MenuItem value="">All</MenuItem>
                  {registerCodes.map((c) => <MenuItem key={c.code} value={String(c.code)}>{registerLabel(c.code, c.name)}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid item xs={6} md={2}>
                <TextField id="e2i-enq-from" type="date" label="Enquiry date from" value={from} onChange={(e) => setFrom(cleanDateInput(e.target.value))} InputLabelProps={{ shrink: true }} fullWidth />
              </Grid>
              <Grid item xs={6} md={2}>
                <TextField id="e2i-enq-to" type="date" label="to" value={to} onChange={(e) => setTo(cleanDateInput(e.target.value))} InputLabelProps={{ shrink: true }} inputProps={{ min: fromDate }} fullWidth />
              </Grid>
            </Grid>
          </Paper>

          {listError ? <Alert severity="error">{listError}</Alert> : null}
          {loading ? (
            <Stack direction="row" spacing={1} alignItems="center" sx={{ p: 2 }}><CircularProgress size={18} /><Typography variant="body2">Loading…</Typography></Stack>
          ) : (
            <ResponsiveTable
              ariaLabel="Enquiries"
              columns={columns}
              rows={rows}
              getRowKey={(r) => r.enquiryId}
              onRowClick={(r) => navigate(`/e2i/enquiries/${r.enquiryId}`)}
              emptyMessage={filtered ? 'No enquiry matches the filters' : 'No enquiries yet'}
            />
          )}
        </>
      ) : !error ? (
        <Stack direction="row" spacing={1} alignItems="center"><CircularProgress size={18} /><Typography variant="body2">Loading…</Typography></Stack>
      ) : null}

      <NewEnquiryDialog
        open={newOpen}
        companyId={companyId}
        config={config}
        onClose={() => setNewOpen(false)}
        onCreated={(ws) => { setNewOpen(false); navigate(`/e2i/enquiries/${ws.enquiry.enquiryId}`); }}
      />
    </Box>
  );
};

export default E2IEnquiries;
